import { Cancel, Edit, Save } from '@mui/icons-material';
import { IconButton, TextField, Tooltip } from '@mui/material';
import { useEnter, useEscape, useKeyPresses } from '@qfa/react-components';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import useEditableTitleStyles from './styles';
import { EditableTitleProps } from './types';
import { useState } from 'react';

const EditableTitle: React.FunctionComponent<EditableTitleProps> = (props) => {
  const {
    isEditMode,
    title,
    onEdit,
    inputValue,
    onChange,
    onSave,
    onCancel = () => null,
    inputRef,
    hideEditIcon = false,
    disableClickToEdit = false,
    isFocusedInput = false,
    showEditHover = false,
    doubleClickEdit = false,
  } = props;

  const [hover, setHover] = useState(false);

  const { t } = useTranslation('common');
  const classes = useEditableTitleStyles(props);

  const onKeyUp = useKeyPresses(useEnter({ callback: onSave }), useEscape({ callback: onCancel }));

  const preventDefault = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const renderEditMode = () => {
    return (
      <span className={classes.editableWrapper}>
        <TextField
          className={classes.textField}
          value={inputValue}
          onKeyUp={onKeyUp}
          // when use in a menu this prevents the s key from moving focus.
          onKeyDown={(e) => e.stopPropagation()}
          InputProps={{
            classes: {
              root: clsx(
                { [classes.focusedInputRoot]: isFocusedInput },
                { [classes.inputRoot]: !isFocusedInput }
              ),
            },
          }}
          onClick={preventDefault}
          inputRef={inputRef}
          onChange={onChange}
        />
        <span className={classes.buttonWrapper}>
          <Tooltip title={t('save')}>
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSave();
              }}
              size='small'
              className={classes.button}
            >
              <Save fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('cancel')}>
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancel();
              }}
              size='small'
              className={classes.button}
            >
              <Cancel fontSize='small' />
            </IconButton>
          </Tooltip>
        </span>
      </span>
    );
  };

  const renderTitle = () => {
    return (
      <Tooltip title={disableClickToEdit ? '' : t('edit')} placement='bottom-end'>
        <span
          onMouseLeave={() => setHover(false)}
          onMouseEnter={() => setHover(true)}
          style={{ cursor: 'pointer' }}
          className={`${classes.editableWrapper} ${classes.subtleIcon}`}
          onClick={(event) => {
            if (event.detail === 2 && doubleClickEdit) {
              onEdit?.();
            } else if (!disableClickToEdit) {
              onEdit?.();
            }
          }}
        >
          {title}
          {!hideEditIcon && (!showEditHover || (showEditHover && hover)) && (
            <Edit onClick={onEdit} className={classes.editIcon} fontSize='small' />
          )}
        </span>
      </Tooltip>
    );
  };

  const renderEditableTitle = () => {
    return <>{isEditMode ? renderEditMode() : renderTitle()}</>;
  };

  return renderEditableTitle();
};

export default EditableTitle;
